import {
  Check as CheckIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Language as LanguageIcon,
} from '@mui/icons-material'
import {
  Button,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { useIntercom } from 'react-use-intercom'

import i18n from '~/i18n'
import {
  PLATFORM_LANGUAGES,
  type PlatformLanguage,
} from '~/utils/types/i18n'

export default function PlatformLanguageSelector() {
  const intercom = useIntercom()

  const {
    t,
    i18n: { language },
  } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const languageOptions = PLATFORM_LANGUAGES.map(lang => ({
    code: lang.langKey,
    label: lang.langName,
  }))

  const selectedLanguageLabel = languageOptions.find(
    lang => lang.code === language,
  )?.label

  function handleLanguageChange(nextLanguage: PlatformLanguage) {
    setIsOpen(false)

    localStorage.setItem('language', nextLanguage)

    void i18n.changeLanguage(nextLanguage)
    intercom.update({ languageOverride: nextLanguage })
  }

  return (
    <Stack gap={0.25} sx={{ minWidth: { xs: 128, sm: 144 } }}>
      <Button
        id="language-selector"
        aria-controls={isOpen ? 'language-menu' : undefined}
        aria-expanded={isOpen ? 'true' : undefined}
        aria-haspopup="menu"
        aria-label={t('views.languageFilter.label')}
        endIcon={
          isOpen ? (
            <KeyboardArrowUp
              sx={{ color: '#6f7775', fontSize: 19 }}
            />
          ) : (
            <KeyboardArrowDown
              sx={{ color: '#6f7775', fontSize: 19 }}
            />
          )
        }
        startIcon={
          <LanguageIcon sx={{ color: '#50aa47', fontSize: 18 }} />
        }
        onClick={event => {
          setIsOpen(!isOpen)
          setAnchorEl(event.currentTarget)
        }}
        sx={{
          minHeight: 36,
          justifyContent: 'space-between',
          px: 1.25,
          borderRadius: 2,
          color: '#5f6664',
          '&:hover': {
            backgroundColor: 'rgba(80, 170, 71, 0.08)',
          },
          '& .MuiButton-startIcon': { mr: 0.75 },
          '& .MuiButton-endIcon': { ml: 0.5 },
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          {selectedLanguageLabel ?? 'English'}
        </Typography>
      </Button>

      <Menu
        id="language-menu"
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => {
          setIsOpen(false)
        }}
        MenuListProps={{ 'aria-labelledby': 'language-selector' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 190,
              border: '1px solid #e3e9e4',
              borderRadius: 2.5,
              boxShadow: '0 12px 28px rgba(34, 55, 39, 0.14)',
              backgroundColor: '#fff',
            },
          },
        }}
      >
        {languageOptions.map(lang => (
          <MenuItem
            key={lang.code}
            selected={lang.code === language}
            onClick={() => {
              handleLanguageChange(lang.code)
            }}
            sx={{
              minHeight: 40,
              justifyContent: 'space-between',
              gap: 2,
              px: 1.5,
              borderRadius: 1.5,
              mx: 0.5,
              my: 0.25,
              '&.Mui-selected': {
                backgroundColor: 'rgba(80, 170, 71, 0.1)',
              },
              '&.Mui-selected:hover': {
                backgroundColor: 'rgba(80, 170, 71, 0.16)',
              },
            }}
          >
            <Typography
              component="span"
              sx={{ color: '#5f6664', fontSize: 14, fontWeight: 600 }}
            >
              {lang.label}
            </Typography>
            {lang.code === language && (
              <CheckIcon sx={{ color: '#50aa47', fontSize: 18 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  )
}
